// Encrypted backup file for the organizer. Encryption happens in the browser with a passphrase only the user knows;
// we never see the passphrase or the file. Lose the passphrase and the backup can't be opened.
import type { YearFile } from "@ftr/tax-core";
import type { StoredLicense } from "./license.ts";
import type { SavedSnapshot } from "./yearStore.ts";

const ITERATIONS = 600_000;

export interface BackupContents {
  kind: "ftr.organizer-backup";
  format: 1;
  savedAt: string;
  yearFiles: YearFile[];
  snapshots: SavedSnapshot[];
  /** Report licenses, so a restore on a new device keeps the report unlocked. Older backups don't have them. */
  licenses?: StoredLicense[];
}

interface BackupFile {
  kind: "ftr.encrypted-backup";
  format: 1;
  kdf: { name: "PBKDF2"; hash: "SHA-256"; iterations: number; salt: string };
  cipher: { name: "AES-GCM"; iv: string };
  data: string;
}

const enc = new TextEncoder();
const dec = new TextDecoder();

function b64(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

function unb64(s: string): Uint8Array<ArrayBuffer> {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function keyFrom(passphrase: string, salt: Uint8Array<ArrayBuffer>, iterations: number): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey("raw", enc.encode(passphrase), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey({ name: "PBKDF2", hash: "SHA-256", salt, iterations }, base, { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
}

export async function encryptBackup(contents: BackupContents, passphrase: string): Promise<string> {
  if (passphrase.length < 10) throw new Error("Use a passphrase of at least 10 characters.");
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await keyFrom(passphrase, salt, ITERATIONS);
  const data = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, enc.encode(JSON.stringify(contents))));
  const file: BackupFile = {
    kind: "ftr.encrypted-backup",
    format: 1,
    kdf: { name: "PBKDF2", hash: "SHA-256", iterations: ITERATIONS, salt: b64(salt) },
    cipher: { name: "AES-GCM", iv: b64(iv) },
    data: b64(data),
  };
  return JSON.stringify(file);
}

export async function decryptBackup(text: string, passphrase: string): Promise<BackupContents> {
  let file: BackupFile;
  try {
    file = JSON.parse(text) as BackupFile;
  } catch {
    throw new Error("That isn't a Futures Tax Report backup file.");
  }
  if (file.kind !== "ftr.encrypted-backup" || file.format !== 1) throw new Error("That isn't a Futures Tax Report backup file.");
  const key = await keyFrom(passphrase, unb64(file.kdf.salt), file.kdf.iterations);
  let plain: ArrayBuffer;
  try {
    plain = await crypto.subtle.decrypt({ name: "AES-GCM", iv: unb64(file.cipher.iv) }, key, unb64(file.data));
  } catch {
    throw new Error("Wrong passphrase, or the file was changed.");
  }
  const contents = JSON.parse(dec.decode(plain)) as BackupContents;
  if (contents.kind !== "ftr.organizer-backup") throw new Error("That backup is in a format this version can't read.");
  return contents;
}

/** Offer a file to save. The bytes never leave the browser. */
export function download(filename: string, text: string, type = "application/json") {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
